import { useState, useCallback, useEffect } from 'react'
import { datadogLogs } from '@datadog/browser-logs'
import { AUTO_BET_CONFIG } from 'src/config'

interface AutoBetState {
  isEnabled: boolean
  isActive: boolean
}

interface UseAutoBetProps {
  onBet: (amount: string, autoCashoutMultiplier?: number) => Promise<void>
  betAmount: string
  insufficientBalance: boolean
  hasExistingBet: boolean
  isRaceReady: boolean
  onReset?: () => void
  playerId: string
}

interface StopBettingParams {
  reason?: string
  scope?: string
}

export const useAutoBet = ({
  onBet,
  betAmount,
  insufficientBalance,
  hasExistingBet,
  isRaceReady,
  playerId
}: UseAutoBetProps) => {
  const [state, setState] = useState<AutoBetState>({
    isEnabled: false,
    isActive: false
  })

  const toggle = useCallback(() => {
    setState((prev) => ({
      isEnabled: !prev.isEnabled,
      isActive: false
    }))

    datadogLogs.logger.info('auto_bet', {
      user_action: {
        type: 'toggled',
        playerId
      }
    })
  }, [playerId])

  const startBetting = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isActive: true
    }))

    datadogLogs.logger.info('auto_bet', {
      user_action: {
        type: 'started',
        playerId
      }
    })
  }, [playerId])

  const stopBetting = useCallback(({ reason, scope }: StopBettingParams) => {
    setState((prev) => ({
      ...prev,
      isEnabled: false,
      isActive: false
    }))

    datadogLogs.logger.info('auto_bet', {
      user_action: {
        type: 'stopped',
        scope,
        reason,
        playerId
      }
    })
  }, [playerId])

  // Handle auto betting when race is ready
  useEffect(() => {
    if (!state.isEnabled || !state.isActive) {
      return
    }

    if (insufficientBalance) {
      stopBetting({
        scope: 'betPanel',
        reason: 'insufficientBalance'
      })
      return
    }

    if (isRaceReady && !hasExistingBet) {
      const timer = setTimeout(() => {
        void onBet(betAmount)
      }, AUTO_BET_CONFIG.BET_COUNTDOWN * 1000)

      datadogLogs.logger.info('auto_bet', {
        user_action: {
          type: 'bet_placed',
          amount: betAmount,
          playerId
        }
      })

      return () => { clearTimeout(timer) }
    }
  }, [
    isRaceReady, 
    hasExistingBet, 
    state.isEnabled, 
    state.isActive, 
    insufficientBalance, 
    betAmount, 
    onBet, 
    stopBetting, 
    playerId
  ])

  return {
    isEnabled: state.isEnabled,
    isActive: state.isActive,
    toggle,
    startBetting,
    stopBetting
  }
}
